import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import UnifiedLayout from "@enerex/unified-ui-react/Layout";
import { EnerexAppEnum } from "@enerex/unified-ui-utils";
import SettingsIcon from '@enerex/unified-ui-react/icons/SettingsIcon';
import LogoutIcon from "@enerex/unified-ui-react/icons/LogoutIcon";
import AppcuesMenu from "components/AppcuesMenu";
import ClientDropdown from "components/ClientDropdown";
import NotificationDropdown from "components/NotificationDropdown";
import profileMenus from "constants/profileMenus";
import { getMenuItems } from "helpers/menu";
import { getNotificationMessage } from "pages/notifications/utils";
import { useAuth } from "providers/CustomAuthProvider";
import NotificationHub from "utils/notificationReceiver";

const renderIcon = (icon) => {
  if (!icon) return undefined;
  if (React.isValidElement(icon)) return icon;
  return <i className={icon} />;
};

const renderIconWithClassName = (icon, className) => {
  if (!icon) return undefined;
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      className: [icon.props.className, className].filter(Boolean).join(" "),
    });
  }
  return renderIcon(`${icon} ${className}`);
};

const toUnifiedMenuItems = (items) =>
  (items || []).map(({ icon, children, disabled, ...item }) => ({
    ...item,
    icon: renderIcon(icon),
    children: children ? toUnifiedMenuItems(children) : undefined,
  }));

const PremiumStatusBadge = ({ isPaid }) => (
  <span
    style={{
      alignItems: "center",
      backgroundColor: "rgba(112, 0, 150, 0.12)",
      border: "1px solid rgba(112, 0, 150, 0.18)",
      borderRadius: 4,
      color: "var(--ct-primary)",
      display: "inline-flex",
      fontFamily: "var(--font-subheading, 'Palanquin Dark', sans-serif)",
      fontSize: 10,
      fontWeight: 700,
      height: 20,
      lineHeight: 1,
      padding: "0 8px",
      whiteSpace: "nowrap",
    }}
  >
    {isPaid ? "Paid" : "Freemium"}
  </span>
);

const HeaderClientSelector = ({ premiumStatus = null, showClientSelector = false }) => {
  return (
    <div className="d-flex align-items-center justify-content-end gap-3 px-3">
      {showClientSelector && <ClientDropdown />}
      {premiumStatus && <PremiumStatusBadge isPaid={premiumStatus === "paid"} />}
    </div>
  );
};

const isCondensedSidebar = () =>
  document.documentElement.getAttribute("data-sidenav-size") === "condensed";

const UnifiedAppLayout = () => {
  const {
    user,
    isAuthenticated,
    isAdmin,
    isSystemAdmin,
    hasDatahubAccess,
    hasModuleAccess,
    premiumModuleName,
    moduleIsPremium,
    modulePremiumLoading,
    logout,
  } = useAuth();
  const [notificationMessages, setNotificationMessages] = useState([]);
  const [isNotificationReaded, setIsNotificationReaded] = useState(false);

  useEffect(() => {
    const handleCollapsedSidebarWheel = (event) => {
      if (!isCondensedSidebar()) return;

      const sidebar = document.querySelector(".leftside-menu");
      if (!sidebar?.contains(event.target)) return;

      const scrollWrapper = sidebar.querySelector(".simplebar-content-wrapper");
      if (!scrollWrapper) return;

      const maxScrollTop = scrollWrapper.scrollHeight - scrollWrapper.clientHeight;
      if (maxScrollTop <= 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const nextScrollTop = Math.min(
        Math.max(scrollWrapper.scrollTop + event.deltaY, 0),
        maxScrollTop
      );

      if (nextScrollTop !== scrollWrapper.scrollTop) {
        scrollWrapper.scrollTop = nextScrollTop;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener("wheel", handleCollapsedSidebarWheel, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener("wheel", handleCollapsedSidebarWheel, {
        capture: true,
      });
    };
  }, []);

  const onReceived = useCallback((rawMessage) => {
    const payload =
      typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
    const records = Array.isArray(payload?.Records)
      ? payload.Records
      : payload
        ? [payload]
        : [];

    const getNotificationTitle = (eventType, message) => {
      if (eventType === "New") return "New License Registered";
      if (eventType === "Renewal") return "License Renewed";
      if (eventType === "Expiry") return "License Expiring Soon";
      if (eventType === "esiidExport") return message;
      if (eventType === "eclExport") return message;
      return "New Notification";
    };

    const mappedMessages = records.map((raw) => {
      const notificationMessage = getNotificationMessage(raw, false);
      return {
        id: raw.Id,
        isRead: raw.isRead,
        text: notificationMessage,
        subText: "1 min ago",
        icon: "mdi mdi-comment-account-outline",
        bgColor: raw.isRead === 0 ? "info" : "warning",
        notificationObject: raw,
        title: getNotificationTitle(raw.eventType, raw.message),
      };
    });

    if (Array.isArray(payload?.Records)) {
      setNotificationMessages(mappedMessages);
      setIsNotificationReaded(mappedMessages.length > 0);
      return;
    }

    if (mappedMessages.length > 0) {
      setNotificationMessages((current) => [mappedMessages[0], ...current]);
      setIsNotificationReaded(true);
    }
  }, []);

  const menuItems = useMemo(
    () =>
      toUnifiedMenuItems(
        getMenuItems(isAdmin, hasDatahubAccess, hasModuleAccess, isSystemAdmin)
      ),
    [isAdmin, hasDatahubAccess, hasModuleAccess, isSystemAdmin]
  );

  const profileMenuSections = useMemo(
    () => [
      {
        title: `Welcome ${user?.given_name || ""} !`,
        items: [
          ...profileMenus
            .filter((item) => (item?.requireAdminAccess ? isAdmin : true))
            .map((item) => ({
              label: item.label,
              icon: renderIconWithClassName(item.icon, "me-1"),
              redirectTo: item.redirectTo,
            })),
          {
            label: "Logout",
            icon: <LogoutIcon className="me-1" />,
            onClick: () => logout({ returnTo: `https://${window.location.host}` }),
          },
        ],
      },
    ],
    [isAdmin, logout, user?.given_name]
  );

  const topbarItems = useMemo(
    () => [
      <AppcuesMenu key="appcues" />,
      <NotificationDropdown
        key="notifications"
        notifications={notificationMessages}
        isReaded={isNotificationReaded}
        onOpen={() => setIsNotificationReaded(false)}
      />,
      <Link
        key="settings"
        to="/settings"
        style={{
          alignItems: "center",
          display: "inline-flex",
          height: 32,
          justifyContent: "center",
          width: 32,
        }}
      >
        <SettingsIcon fontSize={20} style={{ color: "#6c757d", padding: 5 }} />
      </Link>,
    ].filter(Boolean),
    [isNotificationReaded, notificationMessages]
  );

  const premiumStatus =
    isSystemAdmin && premiumModuleName && !modulePremiumLoading
      ? moduleIsPremium === false
        ? "freemium"
        : "paid"
      : null;
  const showHeaderControls = isSystemAdmin || Boolean(premiumStatus);

  if (!isAuthenticated || hasDatahubAccess !== true) {
    return <Outlet />;
  }

  return (
    <NotificationHub onReceived={onReceived}>
      <UnifiedLayout
        enerexApp={EnerexAppEnum.DataHub}
        menuItems={menuItems}
        customHeader={
          showHeaderControls ? (
            <HeaderClientSelector
              showClientSelector={isSystemAdmin}
              premiumStatus={premiumStatus}
            />
          ) : null
        }
        profileMenuSections={profileMenuSections}
        showMenuToggleButton
        topbarItems={topbarItems}
        user={{
          name: user?.name || "",
          title: user?.email || "",
          imageURL: user?.picture || "",
        }}
      />
    </NotificationHub>
  );
};

export default UnifiedAppLayout;
